<script>
import { mapState } from 'vuex'
import { required, email } from 'vuelidate/lib/validators'
import VSubmitButton from '@/components/VSubmitButton'
import VFormField from '@/components/VFormField'
import VFadeTransition from '@/components/VFadeTransition'
import VImageTransition from '@/components/VImageTransition'
import api from '@/api'
const VWarningMessage = () => import('@/components/VWarningMessage')

export default {
  name: 'ForgotPasswordScreenForm',

  components: {
    VFormField,
    VWarningMessage,
    VSubmitButton,
    VFadeTransition,
    VImageTransition,
  },

  data() {
    return {
      emailValue: '',
      warningMessage: '',
      validationMessage: '',
      emailNotFound: true
    }
  },

  computed: {
    ...mapState({
      clientLogo: 'clientLogo',
      clientName: 'clientName'
    }),
    disableSubmitButton() {
      if (!this.$v.emailValue.$dirty) return false
      return this.emailInvalid
    },
    emailInvalid() {
      return !this.$v.emailValue.required || !this.$v.emailValue.email
    },
  },

  validations: {
    emailValue: {
      required: function (value) {
        if (required(value)) return true
        this.validationMessage = this.$t('requiredField')
        return false
      },
      email: function (value) {
        if (email(value)) return true
        this.validationMessage = this.$t('emailInvalid')
        return false
      },
      emailNotFound() {
        return this.emailNotFound
      }
    },
  },

  methods: {
    async forgotPassword() {
      this.$v.$touch()
      if (this.emailInvalid) {
        return;
      }

      try {
        await this.$store.dispatch('START_LOADING')
        await api.requestPassword(this.emailValue)
        await this.$store.dispatch('STOP_LOADING')
        this.validationMessage = ''
        this.warningMessage = this.$t('emailRecoverSuccess')
        this.emailNotFound = true
      } catch(error) {
        await this.$store.dispatch('STOP_LOADING')
        this.validationMessage = this.$t('emailNotFound')
        this.emailNotFound = false
      }
    },
  },

}
</script>

<template>
  <form
    class="forgot-password"
    @submit.prevent="forgotPassword"
  >

    <div class="forgot-password__logo-content">
      <VImageTransition
        :image="clientLogo"
        :text="clientName"
        small
      />
    </div>

    <div class="forgot-password__content">
      <div class="forgot-password__warning-content">
        <VWarningMessage
          v-if="warningMessage && !validationMessage"
          class="forgot-password__warning"
          success
        >
          {{ warningMessage }}
        </VWarningMessage>
      </div>

      <div class="forgot-password__fields">
        <div class="forgot-password__title">
          {{ $t('forgotPassword') }}
        </div>

        <div class="forgot-password__description">
          {{ $t('forgotPasswordDescription') }}
        </div>

        <VFormField
          :label="$i18n.t('email')"
          v-model="emailValue"
          :validation="$v.emailValue"
          :validation-message="validationMessage"
          name="email"
          autofocus
        />
      </div>
    </div>

    <div class="forgot-password__footer">
      <VSubmitButton
        :disabled="disableSubmitButton"
        class="forgot-password__footer-submit"
      >
        {{ $t('createNewPassword') }}
      </VSubmitButton>

      <router-link
        :to="{ name: 'loginScreen' }"
        class="inv-text-link"
      >
        {{ $t('backToMainScreen') }}
      </router-link>
    </div>
  </form>
</template>

<style scoped>
.forgot-password {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: var(--space-24);
  margin-bottom: var(--space-24);
}

.forgot-password__content {
  max-width: 288px;
  width: 100%;
  height: 300px;
  margin-bottom: 36px;
}

.forgot-password__logo-content {
  min-height: 56px;
  max-height: 56px;
}

.forgot-password__warning-content {
  min-height: 50px;
  padding-top: 40px;
}

.forgot-password__fields {
  display: flex;
  flex-direction: column;
  align-content: center;
}

.forgot-password__warning + .forgot-password__fields {
  margin-top: 14px;
}

.forgot-password__title {
  text-align: center;
  font-weight: bold;
  font-size: 18px;
  color: var(--color-default--dark);
}

.forgot-password__description {
  max-width: 219px;
  margin-top: 5px;
  margin-bottom: 8px;
  font-size: 14px;
  color: var(--color-system--dark);
  align-self: center;
  text-align: center;
}

.forgot-password__footer {
  text-align: center;
  max-width: 288px;
  width: 100%;
}

.forgot-password__footer-submit {
  margin-bottom: 24px;
}
</style>
